import { getSupabase } from './supabase';
import { logoutThunk } from '../ducks/auth.duck';
import * as log from './log';

/**
 * Reconcile drift between the Supabase (`sb-*`) and Sharetribe (`st-*`)
 * sessions in the browser.
 *
 * Auth state is normally mutated server-side in a single request, so the
 * two cookie sets stay in sync on the happy path. This watcher exists for
 * the off-path cases:
 *
 * - Supabase session expires or gets revoked (admin action, Supabase
 *   dashboard) → `onAuthStateChange` fires `SIGNED_OUT` while Sharetribe is
 *   still authenticated. Cascade logout so the browser doesn't sit with a
 *   half-valid auth state.
 *
 * - Sharetribe `authInfo` rehydration on page load reports the user as
 *   no longer authenticated (token expired, account deleted, banned)
 *   while Supabase is still happy. Same fix: cascade logout.
 *
 * A transient token-refresh failure (a network blip during Supabase's
 * page-load session recovery) can surface as `SIGNED_OUT` even though the
 * session recovers moments later, and a momentary store change can look like
 * a Sharetribe deauth. To avoid logging out a perfectly valid user, both
 * directions re-verify the apparent sign-out a few times before cascading;
 * a genuine revocation still cascades, just a couple of seconds later.
 *
 * The watcher hits `/api/auth/logout` to clear both cookie sets in one
 * request, then dispatches the Redux logout to clear in-memory state.
 *
 * @param {import('redux').Store} store the configured Redux store
 * @returns {() => void} unsubscribe function
 */
const CONFIRM_ATTEMPTS = 3;
const CONFIRM_DELAY_MS = 500;

const wait = ms => new Promise(resolve => setTimeout(resolve, ms));

export const installAuthDriftWatcher = store => {
  const supabase = getSupabase();
  if (!supabase) return () => {};

  let cascading = false;

  const cascadeLogout = async reason => {
    if (cascading) return;
    cascading = true;
    try {
      log.error(new Error(`auth drift detected: ${reason}`), 'auth-drift-cascade');
      // logoutThunk hits /api/auth/logout, which clears both cookie sets
      // and dispatches clearCurrentUser on success.
      await store.dispatch(logoutThunk()).catch(() => {});
    } finally {
      cascading = false;
    }
  };

  const stAuthed = () => store.getState().auth?.isAuthenticated;

  // Re-check the Supabase session a few times; a transient refresh failure
  // recovers, a real sign-out stays gone. Returns true only when genuinely gone.
  const supabaseSignOutConfirmed = async () => {
    for (let attempt = 1; attempt <= CONFIRM_ATTEMPTS; attempt++) {
      await wait(CONFIRM_DELAY_MS);
      const { data: current } = await supabase.auth.getSession();
      if (current?.session) return false;
      const refreshed = await supabase.auth.refreshSession().catch(() => null);
      if (refreshed?.data?.session) return false;
    }
    return true;
  };

  // Supabase → Sharetribe direction.
  const { data: subscription } = supabase.auth.onAuthStateChange((event, session) => {
    const isSignOut = event === 'SIGNED_OUT' || (event === 'TOKEN_REFRESHED' && !session);
    if (!isSignOut || !stAuthed()) return;
    supabaseSignOutConfirmed().then(confirmed => {
      if (confirmed && stAuthed()) cascadeLogout(`supabase ${event}`);
    });
  });

  // Sharetribe → Supabase direction. Watch for st-* going from authed to
  // unauthed while Supabase still has a session.
  let prevSt = stAuthed();
  const unsubStore = store.subscribe(async () => {
    const next = stAuthed();
    const wasAuthed = prevSt;
    prevSt = next;
    if (!wasAuthed || next) return;

    // Re-verify the deauth persists (not a momentary store blip).
    for (let attempt = 1; attempt <= CONFIRM_ATTEMPTS; attempt++) {
      await wait(CONFIRM_DELAY_MS);
      if (stAuthed()) return;
    }
    const { data } = await supabase.auth.getSession();
    if (data?.session) cascadeLogout('sharetribe deauth with live supabase session');
  });

  return () => {
    subscription?.subscription?.unsubscribe?.();
    unsubStore();
  };
};
